/**
 * The tenant module config (brief §4.2): what ONE tenant changed about the
 * built-in catalog. It is one jsonb document in that tenant's
 * `hub_settings`, key `modules` (Asian Trails: `public`, the demo: `gloop`),
 * read by every signed-in user through `GET /api/modules` and written only by
 * `PUT /api/admin/modules` (AdminOnly, validated against the catalog).
 *
 * Missing or empty means the catalog's defaults, which is Asian Trails as it
 * has always been. The document only ever holds DIFFERENCES:
 *
 *   modules.<id>   enabled, label, icon, ink, access, groups.<groupId>.heading
 *   pages.<id>     enabled, label           (page id = '<module>.<slug>')
 *   features.<id>  enabled                  ('coportal.logs.key-agent-filter')
 *   order          the Hub order of the modules (unlisted ones follow)
 *   installed      template instances (brief §5.3, `templates.ts`): the
 *                  code-backed templates in use (where they live, their
 *                  settings) and the team modules
 *
 * The API reads the same shape (api/Modules/Catalog/ModuleConfig.cs) and
 * resolves it with the same rules; `config.test.ts` and `ModuleConfigTests`
 * run one shared demo-like document through both sides.
 */
import type { Catalog, ModuleAccess } from './catalog';

export interface TenantModuleConfig {
  version: 1;
  modules: Record<string, ModuleOverride>;
  pages: Record<string, PageOverride>;
  features: Record<string, FeatureOverride>;
  order: string[];
  installed: unknown[];
}

export interface ModuleOverride {
  enabled?: boolean;
  /** The module's name wherever people read it (1-40 characters). */
  label?: string;
  /** A key of `MODULE_ICONS`. */
  icon?: string;
  /** One of {@link MODULE_INKS}: the module's printed ink, never a free colour. */
  ink?: string;
  access?: ModuleAccess;
  groups?: Record<string, { heading?: string }>;
}

export interface PageOverride {
  enabled?: boolean;
  label?: string;
}

export interface FeatureOverride {
  enabled?: boolean;
}

export const EMPTY_MODULE_CONFIG: TenantModuleConfig = Object.freeze({
  version: 1,
  modules: {},
  pages: {},
  features: {},
  order: [],
  installed: [],
}) as TenantModuleConfig;

/** Modules a tenant can never switch off (the way back in, and the person's own settings). */
export const ALWAYS_ON_MODULES: readonly string[] = ['admin', 'preferences'];

export const LABEL_MAX = 40;

/**
 * The printed inks a tenant may give a module (brief §5.2: only the `--c-*`
 * inks, never a free colour picker, so Lamplight holds). The API's
 * `TenantModuleConfig.Inks` is the same list (a test holds them together).
 */
export const MODULE_INKS = [
  '--c-cs',
  '--c-res',
  '--c-lemax',
  '--c-contract',
  '--c-hr',
  '--c-it',
  '--c-finance',
  '--c-data',
  '--c-ecom',
  '--c-bd',
  '--c-product',
  '--c-mgmt',
  '--c-ai',
  '--c-admin',
  '--c-prefs',
] as const;

/** The template id of a team module (the light-module pattern, brief §2.5). */
export const TEAM_TEMPLATE = 'team-module';

/** Words a team module's id (its `/m/<id>` route) may never be. */
export const RESERVED_MODULE_IDS: readonly string[] = [
  'm', 'api', 'admin', 'login', 'logout', 'dashboard', 'forbidden', 'my-notes', 'agent', 'cs',
  'transfers', 'guide', 'guides', 'employeeportal', 'uploads', 'new', 'settings', 'modules',
];

export const TEAM_ID = /^[a-z][a-z0-9-]{1,30}$/;

/**
 * One entry of `installed` (brief §5.3).
 *
 *   code template  `{ template: 'bug-tracker', parent?: 'it', group?, label?, config?, at? }`
 *                  one per tenant; what it switches on, and its name, are
 *                  ordinary overrides (`templates.ts`); the entry holds
 *                  where it lives and its settings.
 *   team module    `{ template: 'team-module', id: 'quality', label, icon, ink, at? }`
 *                  a Hot Plate board scoped to its id and My Notes, at `/m/<id>`.
 */
export interface InstalledEntry {
  template: string;
  id?: string;
  label?: string;
  icon?: string;
  ink?: string;
  /** The module it lives in; absent / null = its own place. */
  parent?: string | null;
  /** A section of `parent` to join, instead of a section of its own. */
  group?: string;
  config?: Record<string, unknown>;
  /** ISO time it was installed. */
  at?: string;
}

/** A team module, read from `installed`. */
export interface TeamModuleEntry {
  id: string;
  label: string;
  icon: string;
  ink: string;
  at?: string;
}

/** The `installed` entries that have the shape of one (leniently). */
export function installedEntries(cfg: Pick<TenantModuleConfig, 'installed'>): InstalledEntry[] {
  const out: InstalledEntry[] = [];
  for (const v of cfg.installed) {
    const o = obj(v);
    if (!o || typeof o.template !== 'string') continue;
    out.push(o as unknown as InstalledEntry);
  }
  return out;
}

/** The team modules, in install order (only the ones with every field sound). */
export function teamModules(cfg: Pick<TenantModuleConfig, 'installed'>): TeamModuleEntry[] {
  const seen = new Set<string>();
  const out: TeamModuleEntry[] = [];
  for (const e of installedEntries(cfg)) {
    if (e.template !== TEAM_TEMPLATE || typeof e.id !== 'string' || !TEAM_ID.test(e.id) || seen.has(e.id)) continue;
    const l = label(e.label);
    if (!l || typeof e.icon !== 'string' || typeof e.ink !== 'string') continue;
    seen.add(e.id);
    const t: TeamModuleEntry = { id: e.id, label: l, icon: e.icon, ink: e.ink };
    if (typeof e.at === 'string') t.at = e.at;
    out.push(t);
  }
  return out;
}

const ACCESS: readonly ModuleAccess[] = ['standard', 'owner-only', 'role-only', 'always'];

function obj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function label(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  return t.length >= 1 && t.length <= LABEL_MAX ? t : undefined;
}

/**
 * Read whatever `GET /api/modules` returned into a config, LENIENTLY: a field
 * of the wrong type is dropped, never thrown on, so a bad row can only fall
 * back to the Asian Trails default for that one field. (The API refuses a bad
 * document on write; this is the belt to that braces.)
 */
export function normalizeModuleConfig(raw: unknown): TenantModuleConfig {
  const o = obj(raw);
  if (!o) return EMPTY_MODULE_CONFIG;
  const out: TenantModuleConfig = { version: 1, modules: {}, pages: {}, features: {}, order: [], installed: [] };

  for (const [id, v] of Object.entries(obj(o.modules) ?? {})) {
    const m = obj(v);
    if (!m) continue;
    const mo: ModuleOverride = {};
    if (typeof m.enabled === 'boolean') mo.enabled = m.enabled;
    const l = label(m.label);
    if (l) mo.label = l;
    if (typeof m.icon === 'string' && m.icon) mo.icon = m.icon;
    if (typeof m.ink === 'string' && (MODULE_INKS as readonly string[]).includes(m.ink)) mo.ink = m.ink;
    if (typeof m.access === 'string' && (ACCESS as string[]).includes(m.access)) mo.access = m.access as ModuleAccess;
    const groups = obj(m.groups);
    if (groups) {
      const go: Record<string, { heading?: string }> = {};
      for (const [gid, gv] of Object.entries(groups)) {
        const h = label(obj(gv)?.heading);
        if (h) go[gid] = { heading: h };
      }
      if (Object.keys(go).length) mo.groups = go;
    }
    out.modules[id] = mo;
  }
  for (const [id, v] of Object.entries(obj(o.pages) ?? {})) {
    const p = obj(v);
    if (!p) continue;
    const po: PageOverride = {};
    if (typeof p.enabled === 'boolean') po.enabled = p.enabled;
    const l = label(p.label);
    if (l) po.label = l;
    out.pages[id] = po;
  }
  for (const [id, v] of Object.entries(obj(o.features) ?? {})) {
    const f = obj(v);
    if (f && typeof f.enabled === 'boolean') out.features[id] = { enabled: f.enabled };
  }
  if (Array.isArray(o.order)) out.order = o.order.filter((x): x is string => typeof x === 'string');
  if (Array.isArray(o.installed)) out.installed = o.installed;
  return out;
}

/**
 * Everything wrong with a config against a catalog, as readable lines (empty
 * = sound). The web twin of the API's write-time validation: unknown module,
 * group, page or feature ids, labels outside 1-40 characters, an unknown
 * access, an icon outside `icons` (when given), an ink outside
 * {@link MODULE_INKS}, an always-on module switched off, and every
 * `installed` entry (known template, one instance per code template, an
 * allowed parent, settings of the template's schema; a team module's id,
 * name, icon and ink). Same lines, same order, as `TenantModuleConfig.Problems`.
 */
export function moduleConfigProblems(raw: unknown, catalog: Catalog, icons?: readonly string[]): string[] {
  const out: string[] = [];
  const o = obj(raw);
  if (!o) return ['config: not an object'];
  if (o.version !== 1) out.push('config: version is not 1');
  const known = new Set(['version', 'modules', 'pages', 'features', 'order', 'installed']);
  for (const k of Object.keys(o)) if (!known.has(k)) out.push(`config: unknown field "${k}"`);

  const modules = new Map(catalog.modules.map((m) => [m.id, m]));
  const pageIds = new Set<string>();
  const featureIds = new Set<string>();
  for (const m of catalog.modules) {
    for (const f of m.features ?? []) featureIds.add(f.id);
    for (const g of m.groups) {
      for (const p of g.pages) {
        pageIds.add(`${m.id}.${p.slug}`);
        for (const f of p.features ?? []) featureIds.add(f.id);
      }
    }
  }
  const checkLabel = (where: string, v: unknown) => {
    if (v === undefined) return;
    if (typeof v !== 'string' || v.trim().length < 1 || v.trim().length > LABEL_MAX) {
      out.push(`${where}: label must be 1-${LABEL_MAX} characters`);
    }
  };
  const checkEnabled = (where: string, v: unknown) => {
    if (v !== undefined && typeof v !== 'boolean') out.push(`${where}: enabled is not true/false`);
  };

  for (const [id, v] of Object.entries(obj(o.modules) ?? {})) {
    const where = `modules.${id}`;
    const cm = modules.get(id);
    if (!cm) { out.push(`${where}: unknown module`); continue; }
    const m = obj(v);
    if (!m) { out.push(`${where}: not an object`); continue; }
    for (const k of Object.keys(m)) {
      if (!['enabled', 'label', 'icon', 'ink', 'access', 'groups'].includes(k)) out.push(`${where}: unknown field "${k}"`);
    }
    checkEnabled(where, m.enabled);
    if (m.enabled === false && ALWAYS_ON_MODULES.includes(id)) out.push(`${where}: cannot be switched off`);
    checkLabel(where, m.label);
    if (m.icon !== undefined && (typeof m.icon !== 'string' || (icons && !icons.includes(m.icon)))) out.push(`${where}: unknown icon`);
    if (m.ink !== undefined && !isInk(m.ink)) out.push(`${where}: unknown ink`);
    if (m.access !== undefined && !(ACCESS as unknown[]).includes(m.access)) out.push(`${where}: unknown access`);
    for (const [gid, gv] of Object.entries(obj(m.groups) ?? {})) {
      if (!cm.groups.some((g) => g.id === gid)) out.push(`${where}.groups.${gid}: unknown group`);
      else checkLabel(`${where}.groups.${gid}`, obj(gv)?.heading);
    }
  }
  for (const [id, v] of Object.entries(obj(o.pages) ?? {})) {
    const where = `pages.${id}`;
    if (!pageIds.has(id)) { out.push(`${where}: unknown page`); continue; }
    const p = obj(v);
    if (!p) { out.push(`${where}: not an object`); continue; }
    for (const k of Object.keys(p)) if (!['enabled', 'label'].includes(k)) out.push(`${where}: unknown field "${k}"`);
    checkEnabled(where, p.enabled);
    checkLabel(where, p.label);
  }
  for (const [id, v] of Object.entries(obj(o.features) ?? {})) {
    const where = `features.${id}`;
    if (!featureIds.has(id)) { out.push(`${where}: unknown feature`); continue; }
    const f = obj(v);
    if (!f) { out.push(`${where}: not an object`); continue; }
    for (const k of Object.keys(f)) if (k !== 'enabled') out.push(`${where}: unknown field "${k}"`);
    checkEnabled(where, f.enabled);
  }
  const teamIds = new Set<string>();
  if (Array.isArray(o.installed)) {
    for (const e of o.installed) {
      const eo = obj(e);
      if (eo?.template === TEAM_TEMPLATE && typeof eo.id === 'string') teamIds.add(eo.id);
    }
  }
  if (o.order !== undefined) {
    if (!Array.isArray(o.order)) out.push('order: not a list');
    else {
      const seen = new Set<string>();
      for (const id of o.order) {
        if (typeof id !== 'string' || !(modules.has(id) || teamIds.has(id))) out.push(`order: unknown module ${String(id)}`);
        else if (seen.has(id)) out.push('order: a module is listed twice');
        else seen.add(id);
      }
    }
  }
  if (o.installed !== undefined && !Array.isArray(o.installed)) out.push('installed: not a list');
  else if (Array.isArray(o.installed)) out.push(...installedProblems(o.installed, catalog, icons));
  return out;
}

function isInk(v: unknown): boolean {
  return typeof v === 'string' && (MODULE_INKS as readonly string[]).includes(v);
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function installedProblems(list: unknown[], catalog: Catalog, icons?: readonly string[]): string[] {
  const out: string[] = [];
  const templates = new Map(catalog.templates.map((t) => [t.id, t]));
  const moduleIds = new Set(catalog.modules.map((m) => m.id));
  const seenTemplates = new Set<string>();
  const seenTeams = new Set<string>();
  const ALLOWED = ['template', 'id', 'label', 'icon', 'ink', 'parent', 'group', 'config', 'at'];
  const checkLabel = (where: string, v: unknown) => {
    if (typeof v !== 'string' || v.trim().length < 1 || v.trim().length > LABEL_MAX) {
      out.push(`${where}: label must be 1-${LABEL_MAX} characters`);
    }
  };
  const iconOk = (v: unknown) => typeof v === 'string' && (!icons || icons.includes(v));

  list.forEach((raw, i) => {
    const where = `installed[${i}]`;
    const e = obj(raw);
    if (!e) {
      out.push(`${where}: not an object`);
      return;
    }
    for (const k of Object.keys(e)) if (!ALLOWED.includes(k)) out.push(`${where}: unknown field "${k}"`);
    const t = typeof e.template === 'string' ? templates.get(e.template) : undefined;
    if (!t) {
      out.push(`${where}: unknown template`);
      return;
    }
    if (t.kind !== 'code' && t.kind !== 'config') {
      out.push(`${where}: template ${t.id} is not installed here`);
      return;
    }
    if (t.kind === 'code') {
      if (e.id !== undefined && e.id !== t.id) out.push(`${where}: id must be the template id`);
      if (seenTemplates.has(t.id)) out.push(`${where}: ${t.id} is installed twice`);
      seenTemplates.add(t.id);
      const p = e.parent;
      const ownPlace = p === undefined || p === null || p === t.module;
      const parentOk = ownPlace || (typeof p === 'string' && t.allowParent.includes(p));
      if (!parentOk) out.push(`${where}: parent ${typeof p === 'string' ? p : 'none'} is not allowed`);
      if (e.group !== undefined) {
        const host = typeof p === 'string' && parentOk ? catalog.modules.find((m) => m.id === p) : undefined;
        if (!host || !host.groups.some((g) => g.id === e.group)) out.push(`${where}: unknown group ${String(e.group)}`);
      }
      if (e.label !== undefined) checkLabel(where, e.label);
      if (e.icon !== undefined && !iconOk(e.icon)) out.push(`${where}: unknown icon`);
      if (e.ink !== undefined && !isInk(e.ink)) out.push(`${where}: unknown ink`);
      if (e.config !== undefined) {
        const c = obj(e.config);
        if (!c) out.push(`${where}: config is not an object`);
        else {
          const props = (obj((t.config as Record<string, unknown>).properties) ?? {}) as Record<string, Record<string, unknown>>;
          for (const [k, v] of Object.entries(c)) {
            const prop = props[k];
            const cw = `${where}.config.${k}`;
            if (!prop) {
              out.push(`${cw}: unknown setting`);
              continue;
            }
            if (prop.type === 'string') {
              if (typeof v !== 'string' || v.length > 4000) out.push(`${cw}: must be text`);
              else if (prop.format === 'email' && v.trim() && !EMAIL.test(v.trim())) out.push(`${cw}: is not an email address`);
            } else if (prop.type === 'array') {
              const ok = Array.isArray(v) && v.length <= 50 && v.every((x) => typeof x === 'string' && x.length <= 80);
              if (!ok) out.push(`${cw}: must be a list of text`);
            }
          }
        }
      }
    } else {
      const id = e.id;
      if (typeof id !== 'string' || !TEAM_ID.test(id)) out.push(`${where}: id must be a short slug (a-z, 0-9, -)`);
      else if (moduleIds.has(id) || RESERVED_MODULE_IDS.includes(id) || seenTeams.has(id)) out.push(`${where}: id ${id} is taken`);
      if (typeof id === 'string') seenTeams.add(id);
      checkLabel(where, e.label);
      if (!iconOk(e.icon)) out.push(`${where}: unknown icon`);
      if (!isInk(e.ink)) out.push(`${where}: unknown ink`);
      if ((e.parent !== undefined && e.parent !== null) || e.group !== undefined || e.config !== undefined) {
        out.push(`${where}: a team module has no parent`);
      }
    }
    if (e.at !== undefined && typeof e.at !== 'string') out.push(`${where}: at is not a date`);
  });
  return out;
}
